import { lazy, Suspense, useEffect, useRef, type ReactNode } from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigationType,
} from "react-router";
import { Arrow } from "../components/Icons";
import Metadata from "../components/Metadata";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { ExperienceProvider } from "../hooks/useExperience";
import { ThemeProvider } from "../hooks/useTheme";
import { usePageMotion } from "../hooks/usePageMotion";
import Home from "../pages/Home";

const Work = lazy(() => import("../pages/Work"));
const CaseStudy = lazy(() => import("../pages/CaseStudy"));
const About = lazy(() => import("../pages/About"));
const BehindBuild = lazy(() => import("../pages/BehindBuild"));
const Contact = lazy(() => import("../pages/Contact"));
const Legal = lazy(() => import("../pages/Legal"));

function RouteBehavior() {
  const location = useLocation();
  const navigation = useNavigationType();
  const first = useRef(true);
  const previousPath = useRef(location.pathname);
  const previousHash = useRef(location.hash);
  const previousSearch = useRef(location.search);
  const positions = useRef(new Map<string, number>());
  useEffect(() => {
    const key = location.key;
    const save = () => positions.current.set(key, window.scrollY);
    window.addEventListener("scroll", save, { passive: true });
    const queryOnly =
      !first.current &&
      previousPath.current === location.pathname &&
      previousHash.current === location.hash &&
      previousSearch.current !== location.search &&
      navigation !== "POP";
    const focus = !first.current && previousPath.current !== location.pathname;
    const apply = () => {
      if (queryOnly) return true;
      if (location.hash) {
        const target = document.getElementById(
          decodeURIComponent(location.hash.slice(1)),
        );
        if (target) {
          // Use the CSS scroll preference: smooth normally, instant when motion is off.
          target.scrollIntoView({ behavior: "auto", block: "start" });
          return true;
        }
        return false;
      }
      if (focus)
        document.getElementById("main")?.focus({ preventScroll: true });
      window.scrollTo({
        top: navigation === "POP" ? (positions.current.get(key) ?? 0) : 0,
        behavior: "instant",
      });
      return true;
    };
    let observer: MutationObserver | undefined;
    const frame = requestAnimationFrame(() => {
      if (!apply() || document.querySelector(".route-loading")) {
        observer = new MutationObserver(() => {
          if (!document.querySelector(".route-loading") && apply())
            observer?.disconnect();
        });
        observer.observe(document.getElementById("root")!, {
          childList: true,
          subtree: true,
        });
      }
    });
    first.current = false;
    previousPath.current = location.pathname;
    previousHash.current = location.hash;
    previousSearch.current = location.search;
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("scroll", save);
    };
  }, [location.key, location.hash, location.pathname, location.search, navigation]);
  return null;
}

function PageMotion() {
  usePageMotion();
  return null;
}

export function Shell({ children }: { children: ReactNode }) {
  return (
    <ThemeProvider>
      <ExperienceProvider>
        <PageMotion />
        <Navigation />
        <main id="main" tabIndex={-1}>
          {children}
        </main>
        <Footer />
      </ExperienceProvider>
    </ThemeProvider>
  );
}

export function NotFound() {
  return (
    <section className="page-intro container not-found">
      <Metadata title="Page not found | Darren" description="That page couldn’t be found. Head back to Darren’s portfolio to explore his work." noIndex />
      <span className="eyebrow">404 / Off track</span>
      <h1>A small detour.</h1>
      <p>
        I couldn’t find that page. Head back home to find the projects
        and the rest of the site.
      </p>
      <Link className="button primary" to="/">
        Back to home <Arrow diagonal />
      </Link>
    </section>
  );
}

export default function App() {
  return (
    <>
      <RouteBehavior />
      <Shell>
        <Suspense
          fallback={
            <div className="route-loading container" role="status">
              Loading the page…
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/work" element={<Work />} />
            <Route path="/work/:slug" element={<CaseStudy />} />
            <Route path="/about" element={<About />} />
            <Route path="/behind-the-build" element={<BehindBuild />} />
            <Route path="/playground" element={<Navigate to="/behind-the-build" replace />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Legal kind="privacy" />} />
            <Route path="/terms" element={<Legal kind="terms" />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Shell>
    </>
  );
}
